// `/x/<experimentKey>` — the Experiment template, instantiated for one
// experiment, read only, saving nothing.
//
// **Templates are code, forks are rows.** `GET /api/bi/templates/{slug}`
// settles which dashboard this resolves: an AUTHORED one first (committed YAML,
// reviewed in a pull request, ingested by the ordinary registry sync, identical
// for everybody who opens `/x/anything`), falling back to a readable studio row
// so a template can be prototyped in the canvas before it is promoted. This page
// does not re-implement that order, and it does not assemble the query URL from
// `origin` either: the response carries `queryPath`, because the routing rule
// living in two places has exactly one symptom, a 404 on the one dashboard
// everybody opens.
//
// **When the template does not exist.** A 404 resolves to `null`, and the page
// says so, naming the slug. The authored template ships with the registry sync,
// so an environment that has not received it yet is a normal state on the way in
// rather than a fault — the same distinction the catalog draws between "not
// synced yet" and "genuinely zero dashboards".
import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";

import { Button } from "@bi/shared";
import type { ChartSpec, StudioTemplateResponse } from "@bi/shared/types";

import { StudioDashboardView } from "../components/studio/studio-dashboard-view";
import { EmptyState, ErrorState, LoadingState, errorMessage } from "../components/states";
import { EMPLOYEE_EMAIL } from "../config";
import { forkStudioDashboard } from "../lib/api";
import { isChartTile } from "../lib/studio-model";
import { dashboardsQuery, studioTemplateQuery } from "../lib/queries";
import { useStudioDashboardCharts } from "../lib/studio-query";
import {
  EXPERIMENT_PARAMETER,
  EXPERIMENT_TEMPLATE_SLUG,
  applyParameters,
} from "../lib/studio-template";

export function ExperimentDashboardPage({ experimentKey }: { experimentKey: string }) {
  const template = useQuery(studioTemplateQuery(EXPERIMENT_TEMPLATE_SLUG));
  // The SERVER's view of who is signed in, not the build-time constant. In
  // cloud the SPA cannot read the HttpOnly `bi_authority` cookie, so
  // `EMPLOYEE_EMAIL` is whatever was baked in at build time, while the worker
  // publishes to `channel_for_bi(<real email>)` and `POST /pusher/auth` refuses
  // the stale one. The catalog is already in cache before any route renders
  // (`App.tsx`'s `Boot()`), so this costs no round trip.
  const catalog = useQuery(dashboardsQuery);
  const employeeEmail = catalog.data?.employeeEmail || EMPLOYEE_EMAIL;

  if (template.isPending) return <LoadingState label="Loading the Experiment template…" />;
  if (template.isError) {
    return <ErrorState error={template.error} onRetry={() => void template.refetch()} />;
  }

  if (!template.data) {
    return (
      <div className="bi-page">
        <div className="bi-page__head">
          <h1 className="bi-page__title">Experiment {experimentKey}</h1>
        </div>
        <EmptyState
          title="The Experiment template is not in this environment yet"
          message={`This page renders the dashboard with the slug "${EXPERIMENT_TEMPLATE_SLUG}", filled in for one experiment. Nothing carries that slug here yet. The authored template arrives with the registry sync; until then there is nothing to instantiate.`}
        />
      </div>
    );
  }

  return (
    <Instance
      key={template.data.dashboardId}
      experimentKey={experimentKey}
      template={template.data}
      employeeEmail={employeeEmail}
    />
  );
}

/** Every chart the template's LAYOUT places, in layout order.
 *
 *  Read off the layout rather than off `charts`, and it matters more here than
 *  on a studio dashboard: an authored template's `charts` map is the registry
 *  SLICE its layout needs, and asking the query route for a chart the layout
 *  does not place is a 422 for the whole board. */
function placedChartIds(
  template: StudioTemplateResponse,
  charts: Readonly<Record<string, ChartSpec>>,
): string[] {
  const ids: string[] = [];
  for (const row of template.spec.layout.rows) {
    for (const tile of row.tiles) {
      if (isChartTile(tile) && charts[tile.chart_uuid]) ids.push(tile.chart_uuid);
    }
  }
  return ids;
}

function Instance({
  experimentKey,
  template,
  employeeEmail,
}: {
  experimentKey: string;
  template: StudioTemplateResponse;
  employeeEmail: string;
}) {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  // `parameters` entries are IR models, so their fields are snake_case
  // (`filter_id`) while the envelope around them is camelCase — the two-casing
  // rule this whole wire follows, and the one place on this page it shows.
  const instance = useMemo(
    () =>
      applyParameters(template.parameters, template.spec.filters, {
        [EXPERIMENT_PARAMETER]: experimentKey,
      }),
    [template, experimentKey],
  );
  const [filters, setFilters] = useState(instance.filters);
  const chartIds = useMemo(
    () => placedChartIds(template, template.charts),
    [template],
  );
  const { charts: chartStates, annotationIds, retryChart } = useStudioDashboardCharts(
    template.dashboardId,
    // The id a `bi.charts.settled` event carries is the SPEC's uuid, which for a
    // studio template is not the row id in the URL.
    template.spec.uuid,
    chartIds,
    filters,
    employeeEmail,
    template.queryPath,
  );

  // Forking copies a STUDIO ROW. An authored template has none — it is committed
  // YAML — so Customise is only offered where there is something to copy, and
  // the reason is stated rather than the button silently doing nothing. Promoting
  // an authored template into an editable copy needs a backend route that turns
  // its authored charts into built ones; there is no client-side version of that,
  // because a built chart may not carry the SQL metrics an authored one does.
  const forkable = template.origin === "studio";
  const customise = useMutation({
    mutationFn: () =>
      forkStudioDashboard(template.dashboardId, {
        title: `${template.title}: ${experimentKey}`,
      }),
    onSuccess: (resp) =>
      void navigate({ to: "/studio/$id", params: { id: resp.id }, search: { edit: "1" } }),
    onError: (err: unknown) => setError(errorMessage(err)),
  });

  return (
    <div className="bi-page">
      <div className="bi-page__head">
        <h1 className="bi-page__title">
          {template.title}: {experimentKey}
        </h1>
        {forkable ? (
          <Button
            variant="outline"
            tone="neutral"
            size="sm"
            disabled={customise.isPending}
            onClick={() => customise.mutate()}
          >
            {customise.isPending ? "Copying…" : "Customise"}
          </Button>
        ) : (
          <span className="bi-studio-bar__version">
            This template is committed to the repository, so it reads the same for everybody.
          </span>
        )}
      </div>

      {instance.unbound.length > 0 ? (
        // A dashboard showing EVERY experiment under one experiment's name is
        // the failure worth being loud about: the numbers are not wrong, the
        // scope is, and nothing else on the page would say so.
        <p className="bi-studio-error" role="alert">
          This template declares {instance.unbound.join(", ")} but has no control to bind{" "}
          {instance.unbound.length === 1 ? "it" : "them"} to, so the board below is NOT narrowed to{" "}
          {experimentKey}.
        </p>
      ) : null}
      {error ? (
        <p className="bi-studio-error" role="alert">
          {error}
        </p>
      ) : null}

      <StudioDashboardView
        layout={template.spec.layout}
        controls={template.spec.filters}
        charts={template.charts}
        datasets={template.datasets}
        chartStates={chartStates}
        annotationIds={annotationIds}
        filters={filters}
        onFiltersChange={setFilters}
        employeeEmail={employeeEmail}
        onRetry={retryChart}
      />
    </div>
  );
}
